import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { AlertTriangle, FileUp, Loader2, PenLine, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { invoices as invoicesApi } from '@/utils/api'
import { parseGermanNumber } from '@/lib/utils'
import { outgoingInvoiceSchema, type OutgoingInvoiceInput } from '@/schemas/outgoingInvoice'
import { fileUrl } from '../buchhaltung/invoiceUtils'
import { addDaysIso, daysBetweenIso } from './outgoingUtils'

// react-pdf nur nachladen — und NIE ueber das Barrel components/common, sonst
// entstehen zwei pdfjs-Instanzen und die Vorschau bleibt leer.
const FilePreviewPane = lazy(() => import('@/components/common/FilePreviewPane'))

type Step = 'pick' | 'analyzing' | 'review'
type FieldName = keyof OutgoingInvoiceInput

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (invoice: any) => void
}

const EMPTY: OutgoingInvoiceInput = {
  customer_name: '', invoice_number: '', invoice_date: '', gross_amount: '', net_amount: '', vat_amount: '',
  payment_days: '', due_date: '', skonto_enabled: false, skonto_percent: '', skonto_amount: '', skonto_date: '',
  description: '',
}

/** Zahl fuer ein Textfeld vorbelegen — mit Komma, damit parseGermanNumber sie zurueck liest. */
const fmtNum = (n?: number | null) =>
  n == null ? '' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtPct = (n?: number | null) => (n == null ? '' : String(n).replace('.', ','))

// Gleiche Grenzen wie Multer in routes/invoices.js — vorher pruefen spart den
// Upload grosser Dateien, die der Server ohnehin ablehnt.
const ALLOWED_EXT = /\.(pdf|jpe?g|png|webp)$/i
const MAX_BYTES = 100 * 1024 * 1024

interface DuplicateInfo {
  id: number
  invoice_number: string
  recipient_name?: string | null
  supplier_name?: string | null
  invoice_date?: string | null
  gross_amount?: number | null
}

/**
 * Neue Ausgangsrechnung: Datei waehlen → Server liest aus (~20 s, PaddleOCR) →
 * Werte pruefen/korrigieren, Zahlungsziel + Skonto ergaenzen → Speichern.
 * Erst "Speichern" legt die Rechnung an; Abbrechen loescht die abgelegte Datei
 * wieder. Ohne Datei geht es direkt in die manuelle Erfassung.
 */
export default function OutgoingUploadDialog({ open, onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('pick')
  const [file, setFile] = useState<File | null>(null)
  const [upload, setUpload] = useState<{ token: string; name: string } | null>(null)
  const [analysisNote, setAnalysisNote] = useState<string | null>(null)
  const [detected, setDetected] = useState<Set<FieldName>>(new Set())
  const [elapsed, setElapsed] = useState(0)
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Laufende Auswertung: wird der Dialog waehrenddessen geschlossen, kommt das
  // Ergebnis trotzdem an — dann gehoert es zu keinem Dialog mehr und die schon
  // abgelegte Datei muss sofort wieder weg (sonst verwaist sie bis zum Cron).
  const runId = useRef(0)
  // Skontobetrag nur vorschlagen, solange weder der Beleg noch der Nutzer einen
  // gesetzt hat — ein ausgelesener Betrag wird nie aus Brutto × Satz ueberschrieben.
  const skontoAmountFixed = useRef(false)

  const form = useForm<OutgoingInvoiceInput>({
    resolver: zodResolver(outgoingInvoiceSchema),
    defaultValues: EMPTY,
  })

  // Vorschau aus der schon abgelegten Datei, nicht per blob:-URL — die CSP
  // (connect-src 'self') blockt blob: fuer pdf.js.
  const previewUrl = upload ? fileUrl(`invoices/${upload.token}`) : null

  // Seite verlassen, waehrend ein Upload offen ist (Navigation, Dialog-Unmount):
  // Datei verwerfen und eine noch laufende Auswertung ins Leere laufen lassen.
  const uploadRef = useRef(upload)
  uploadRef.current = upload
  useEffect(() => () => {
    runId.current++
    if (uploadRef.current) discard(uploadRef.current.token)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 'analyzing') return
    setElapsed(0)
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [step])

  const discard = (token: string) => {
    invoicesApi.discardOutgoingUpload(token).catch((err: any) => {
      console.error('Ausgangsrechnung: Upload verwerfen fehlgeschlagen:', err?.response?.data || err?.message)
    })
  }

  const reset = () => {
    runId.current++
    setDuplicate(null)
    setStep('pick')
    setFile(null)
    setUpload(null)
    setAnalysisNote(null)
    setDetected(new Set())
    skontoAmountFixed.current = false
    form.reset(EMPTY)
  }

  // Abbrechen: abgelegte, nie gespeicherte Datei wieder entfernen
  const close = () => {
    if (upload) discard(upload.token)
    reset()
    onClose()
  }

  const analyze = async (f: File) => {
    if (!ALLOWED_EXT.test(f.name)) {
      toast.error('Nur PDF oder Bilder (JPG, PNG, WebP) möglich')
      return
    }
    if (f.size > MAX_BYTES) {
      toast.error('Datei ist zu groß (max. 100 MB)')
      return
    }
    const myRun = ++runId.current
    setFile(f)
    setStep('analyzing')
    const fd = new FormData()
    fd.append('file', f)
    try {
      const res = await invoicesApi.analyzeOutgoing(fd)
      const { upload_token, original_name, data, analysis_error, is_outgoing_detected } = res.data
      if (myRun !== runId.current) {
        discard(upload_token)
        return
      }
      setUpload({ token: upload_token, name: original_name })

      const values: OutgoingInvoiceInput = {
        ...EMPTY,
        customer_name: data.customer_name || '',
        invoice_number: data.invoice_number || '',
        invoice_date: data.invoice_date || '',
        gross_amount: fmtNum(data.gross_amount),
        net_amount: fmtNum(data.net_amount),
        vat_amount: fmtNum(data.vat_amount),
        due_date: data.due_date || '',
        payment_days: data.due_date && data.invoice_date ? String(daysBetweenIso(data.invoice_date, data.due_date)) : '',
        skonto_enabled: !!data.skonto_percent,
        skonto_percent: fmtPct(data.skonto_percent),
        skonto_amount: fmtNum(data.skonto_amount),
        skonto_date: data.skonto_date || '',
        description: data.description || '',
      }
      form.reset(values)
      skontoAmountFixed.current = data.skonto_amount != null
      setDetected(new Set((Object.keys(values) as FieldName[]).filter(
        (k) => k !== 'skonto_enabled' && k !== 'payment_days' && values[k] !== '',
      )))
      setAnalysisNote(
        analysis_error
        || (!is_outgoing_detected
          ? 'Der Beleg sieht nicht nach einer compeer-Rechnung aus. Bitte Kunde und Beträge besonders genau prüfen.'
          : null),
      )
      setStep('review')
    } catch (err: any) {
      if (myRun !== runId.current) return
      if (err?.response?.status === 409) {
        toast.error('Diese Datei wurde bereits hochgeladen', {
          description: err.response.data?.existingDocument?.title || undefined,
        })
      } else if (err?.code === 'ECONNABORTED' || err?.response?.status === 504) {
        toast.error('Auslesen hat zu lange gedauert', {
          description: 'Bitte die Werte ohne Datei manuell erfassen oder ein kürzeres PDF (nur die Rechnungsseiten) hochladen.',
        })
      } else {
        toast.error(err?.response?.data?.error || err?.message || 'Auslesen fehlgeschlagen')
      }
      setFile(null)
      setStep('pick')
    }
  }

  // ===== Zahlungsziel <-> Faelligkeit, Skonto-Vorschlag =====
  const invoiceDate = form.watch('invoice_date')
  const skontoEnabled = form.watch('skonto_enabled')

  const setDueFromDays = (daysRaw: string) => {
    const days = parseInt(daysRaw, 10)
    const inv = form.getValues('invoice_date')
    if (inv && Number.isFinite(days)) form.setValue('due_date', addDaysIso(inv, days), { shouldValidate: true })
  }
  const setDaysFromDue = (due: string) => {
    const inv = form.getValues('invoice_date')
    form.setValue('payment_days', inv && due ? String(daysBetweenIso(inv, due)) : '')
  }
  const suggestSkontoAmount = () => {
    if (skontoAmountFixed.current) return
    const gross = parseGermanNumber(form.getValues('gross_amount'))
    const pct = parseGermanNumber(form.getValues('skonto_percent'))
    form.setValue('skonto_amount', gross && pct ? fmtNum(Math.round(gross * pct) / 100) : '')
  }

  const grossW = parseGermanNumber(form.watch('gross_amount'))
  const netW = parseGermanNumber(form.watch('net_amount'))
  const vatW = parseGermanNumber(form.watch('vat_amount'))
  const chainMismatch = grossW !== null && netW !== null && vatW !== null && Math.abs(netW + vatW - grossW) > 0.05

  const submit = async (v: OutgoingInvoiceInput, allowDuplicate = false) => {
    const skontoDays = v.skonto_enabled && v.skonto_date && v.invoice_date
      ? daysBetweenIso(v.invoice_date, v.skonto_date) : null
    try {
      const res = await invoicesApi.createOutgoing({
        upload_token: upload?.token,
        original_name: upload?.name,
        customer_name: v.customer_name,
        invoice_number: v.invoice_number,
        invoice_date: v.invoice_date,
        due_date: v.due_date || null,
        gross_amount: parseGermanNumber(v.gross_amount),
        net_amount: parseGermanNumber(v.net_amount),
        vat_amount: parseGermanNumber(v.vat_amount),
        skonto_percent: v.skonto_enabled ? parseGermanNumber(v.skonto_percent) : null,
        skonto_amount: v.skonto_enabled ? parseGermanNumber(v.skonto_amount) : null,
        skonto_date: v.skonto_enabled ? v.skonto_date || null : null,
        skonto_days: skontoDays && skontoDays > 0 ? skontoDays : null,
        description: v.description,
        allow_duplicate: allowDuplicate,
      })
      toast.success(`Ausgangsrechnung ${res.data.invoice_number || ''} angelegt`.replace('  ', ' '))
      // Datei gehoert jetzt zur Rechnung — beim Schliessen nicht mehr verwerfen
      reset()
      onCreated(res.data)
    } catch (err: any) {
      if (err?.response?.data?.code === 'DUPLICATE_NUMBER') {
        setDuplicate(err.response.data.existing)
        return
      }
      toast.error(err?.response?.data?.error || err?.message || 'Speichern fehlgeschlagen')
    }
  }

  // ===== Feld-Bausteine =====
  const Hint = ({ name }: { name: FieldName }) =>
    detected.has(name) ? (
      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-normal text-primary-600" title="Automatisch aus dem Beleg gelesen — bitte prüfen">
        <Sparkles className="h-3 w-3" /> erkannt
      </span>
    ) : null

  const text = (
    name: FieldName,
    label: string,
    props: Record<string, unknown> = {},
    onValue?: (v: string) => void,
  ) => (
    <FormField control={form.control} name={name} render={({ field }) => (
      <FormItem>
        <FormLabel>{label}<Hint name={name} /></FormLabel>
        <FormControl>
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            onChange={(e) => { field.onChange(e); onValue?.(e.target.value) }}
            {...props}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )} />
  )

  const quickBtn = (label: string, onClick: () => void) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      disabled={!invoiceDate}
      className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-40"
    >
      {label}
    </button>
  )

  const isPdf = !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name))

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className={`max-h-[92vh] overflow-y-auto bg-white ${step === 'review' && previewUrl ? 'max-w-6xl' : 'max-w-2xl'}`}
        // In der Pruefansicht gehen sonst alle Eingaben mit einem Fehlklick daneben verloren
        onInteractOutside={(e) => { if (step !== 'pick') e.preventDefault() }}
      >
        <DialogHeader><DialogTitle>Ausgangsrechnung anlegen</DialogTitle></DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const f = e.dataTransfer.files?.[0]
                if (f) analyze(f)
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10 text-center hover:border-primary-400 hover:bg-primary-50/30"
            >
              <FileUp className="mb-3 h-10 w-10 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">Rechnung als PDF oder Bild hierher ziehen oder klicken</p>
              <p className="mt-1 text-xs text-gray-500">Betrag, Datum, Kunde und Zahlungsziel werden automatisch ausgelesen</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) analyze(f)
                }}
              />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" type="button" onClick={() => setStep('review')}>
                <PenLine className="h-4 w-4" /> Ohne Datei manuell erfassen
              </Button>
              <Button variant="outline" type="button" onClick={close}>Abbrechen</Button>
            </div>
          </div>
        )}

        {step === 'analyzing' && (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary-500" />
            <p className="text-sm font-medium text-gray-700">Rechnung wird ausgelesen…</p>
            <p className="mt-1 text-xs text-gray-500">
              {file?.name} · {elapsed} s — meist 10–30 Sekunden, lange PDFs bis zu einigen Minuten
            </p>
            <Button variant="outline" type="button" className="mt-4" onClick={close}>Abbrechen</Button>
          </div>
        )}

        {step === 'review' && (
          <div className={previewUrl ? 'grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : ''}>
            {file && previewUrl && (
              <div className="h-[45vh] min-h-0 overflow-hidden rounded-lg border border-gray-200 lg:h-[72vh]">
                <Suspense fallback={
                  <div className="flex h-full items-center justify-center bg-gray-100">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                }>
                  <FilePreviewPane url={previewUrl} fileName={file.name} isPdf={isPdf} />
                </Suspense>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit((v) => submit(v))} className="space-y-4">
                {analysisNote && (
                  <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {analysisNote}
                  </p>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">{text('customer_name', 'Kunde / Empfänger *', { placeholder: 'z.B. GOLDBECK Nordost GmbH' })}</div>
                  {text('invoice_number', 'Rechnungsnummer', { placeholder: 'z.B. 26-020' }, () => setDuplicate(null))}
                  {text('invoice_date', 'Rechnungsdatum *', { type: 'date' }, (v) => {
                    const days = form.getValues('payment_days')
                    if (v && days) form.setValue('due_date', addDaysIso(v, parseInt(days, 10)))
                  })}
                  {text('gross_amount', 'Rechnungsbetrag brutto (€) *', { placeholder: '1.190,00', inputMode: 'decimal' }, suggestSkontoAmount)}
                  <div className="grid grid-cols-2 gap-3">
                    {text('net_amount', 'Netto (€)', { placeholder: '1.000,00', inputMode: 'decimal' })}
                    {text('vat_amount', 'USt (€)', { placeholder: '190,00', inputMode: 'decimal' })}
                  </div>
                </div>

                {chainMismatch && (
                  <p className="-mt-2 text-xs text-amber-700">
                    Netto + USt ergibt nicht den Bruttobetrag — bitte prüfen (Netto/USt sind optional).
                  </p>
                )}

                <div className="rounded-lg border p-3">
                  <p className="mb-3 text-sm font-medium">Zahlungsziel</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {text('payment_days', 'Tage ab Rechnungsdatum', { placeholder: 'z.B. 30', inputMode: 'numeric' }, setDueFromDays)}
                    {text('due_date', 'Fällig am', { type: 'date' }, setDaysFromDue)}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-500">Schnell:</span>
                    {[7, 14, 30].map((d) => quickBtn(`${d} Tage`, () => {
                      form.setValue('payment_days', String(d))
                      setDueFromDays(String(d))
                    }))}
                    {!invoiceDate && <span className="text-xs text-gray-400">(erst Rechnungsdatum eintragen)</span>}
                  </div>
                </div>

                <div className="rounded-lg border p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={skontoEnabled}
                      onChange={(e) => {
                        form.setValue('skonto_enabled', e.target.checked, { shouldValidate: form.formState.isSubmitted })
                        if (e.target.checked) suggestSkontoAmount()
                      }}
                      className="rounded text-primary-500"
                    />
                    Skonto gewähren (optional)
                    <Hint name="skonto_percent" />
                  </label>
                  {skontoEnabled && (
                    <>
                      <div className="mt-3 grid gap-4 sm:grid-cols-3">
                        {text('skonto_percent', 'Satz (%)', { placeholder: '2', inputMode: 'decimal' }, suggestSkontoAmount)}
                        {text('skonto_amount', 'Skontobetrag (€)', { placeholder: '23,80', inputMode: 'decimal' }, () => {
                          skontoAmountFixed.current = true
                        })}
                        {text('skonto_date', 'Gültig bis', { type: 'date' })}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-gray-500">Frist:</span>
                        {[7, 10, 14].map((d) => quickBtn(`${d} Tage`, () => {
                          const inv = form.getValues('invoice_date')
                          if (inv) form.setValue('skonto_date', addDaysIso(inv, d), { shouldValidate: form.formState.isSubmitted })
                        }))}
                      </div>
                    </>
                  )}
                </div>

                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschreibung / Bauvorhaben<Hint name="description" /></FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[60px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                  </FormItem>
                )} />

                {duplicate && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="flex items-center gap-1.5 font-medium">
                      <AlertTriangle className="h-4 w-4" /> Rechnungsnummer {duplicate.invoice_number} ist bereits erfasst
                    </p>
                    <p className="mt-1 text-xs">
                      {duplicate.recipient_name || duplicate.supplier_name}
                      {duplicate.invoice_date && ` · ${duplicate.invoice_date.split('-').reverse().join('.')}`}
                      {duplicate.gross_amount != null && ` · ${fmtNum(duplicate.gross_amount)} €`}
                      {' '}— vermutlich doppelt hochgeladen.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setDuplicate(null)}>Nummer korrigieren</Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={form.formState.isSubmitting}
                        onClick={form.handleSubmit((v) => submit(v, true))}
                      >
                        Trotzdem speichern
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" type="button" onClick={close}>Abbrechen</Button>
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Rechnung speichern
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
